import {
	BarChartOutlined,
	BugOutlined,
	CalendarOutlined,
	DownOutlined,
	FileTextOutlined,
	IdcardOutlined,
	LeftOutlined,
	ProfileOutlined,
	QuestionCircleOutlined,
	SettingOutlined,
	UpOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import React, { useEffect, useState } from 'react';
import { useCollapse } from 'react-collapsed';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { devices } from '../../const/devices';
import { ROUTES } from '../../const/routes';
import { useIsDesktop, useIsLaptop, useIsTablet } from '../../hooks/useBreakpoint';
import { useUserFeatureFlags } from '../../hooks/useUserFeatureFlags';
import { UpgradeAccountWidget } from '../UpgradeAccount/UpgradeAccountWidget';

const StyledSettingsSection = styled.div`
	background-color: #f9f9f9;
	margin-left: 0.5em;
	display: flex;
	flex-direction: column;

	button {
		padding-left: 1em;
		padding-bottom: 1em;

		width: 100%;
	}
`;

const AsideSection = styled.div`
	display: flex;
	flex-direction: column;
	padding: 0 0.5rem;

	&:last-of-type {
		padding-bottom: 1rem;
	}
`;

interface ExtendedButtonProps {
	collapsed?: boolean;
}

const StyledButton = styled(Button)<ExtendedButtonProps>`
	width: 100%;
	margin-top: 1em;
	${props =>
		!props.collapsed &&
		css`
			text-align: left;
		`}
`;

const StyledSettingsButton = styled(StyledButton)``;

const ToggleButton = styled(Button)<{ $toggled: boolean }>`
	position: absolute;
	right: -1rem;
	top: 14px;
	z-index: 10;

	transition: rotate 0.3s ease-in-out;

	${({ $toggled }) =>
		$toggled
			? css`
					rotate: 0deg;
				`
			: css`
					rotate: -180deg;
				`};
`;

const StyledAntdSidebar = styled.aside<{ $toggled: boolean }>`
	background-color: #ffffff !important;
	box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.07);
	clip-path: inset(0px -20px 0px 0px);
	border-top: 1px solid #f5f5f5;
	grid-area: sidebar;

	height: 100%;
	z-index: 10;

	transition: width 0.3s ease-in-out;

	width: ${({ $toggled }) => ($toggled ? '250px' : '70px')};

	position: relative;
	display: flex;
	flex-direction: column;
	gap: 6rem;

	@media ${devices.laptop} {
		align-items: center;
	}

	${StyledButton}, ${StyledSettingsSection} {
		& > span:nth-of-type(2) {
			display: ${({ $toggled }) => ($toggled ? 'inline' : 'none')};
		}
	}

	${StyledSettingsButton} {
		& > span:last-of-type {
			margin-inline-start: ${({ $toggled }) => ($toggled ? '0.5rem' : '0')};
		}
	}
`;

const ScrollableContainer = styled.div`
	position: absolute;

	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: 1rem;

	inset: 0;
	overflow-y: auto;
`;

const SideBar: React.FC = () => {
	const { pathname } = useLocation();
	const { getCollapseProps, getToggleProps, isExpanded, setExpanded } = useCollapse({
		defaultExpanded: pathname.startsWith(ROUTES.APP.SETTINGS),
	});
	const [toggled, setToggled] = useState(true);
	const isTablet = useIsTablet();
	const isLaptop = useIsLaptop();
	const isDesktop = useIsDesktop();
	const featureFlags = useUserFeatureFlags();

	useEffect(() => {
		if (isLaptop) {
			setToggled(false);
		}

		if (!isLaptop && isDesktop) {
			setToggled(true);
		}

		if (isExpanded) {
			setToggled(true);
		}
	}, [isDesktop, isLaptop, isExpanded]);

	useEffect(() => {
		if (!toggled) {
			setExpanded(false);
		}
	}, [toggled]);

	return (
		<>
			{!isTablet && (
				<StyledAntdSidebar $toggled={toggled}>
					{isLaptop && (
						<ToggleButton
							onClick={() => setToggled(prev => !prev)}
							type="primary"
							shape="circle"
							icon={<LeftOutlined />}
							$toggled={toggled}
						/>
					)}
					<ScrollableContainer>
						<AsideSection>
							<Link to={ROUTES.APP.TRAININGS}>
								<StyledButton
									type={pathname.startsWith(ROUTES.APP.TRAININGS) ? 'link' : 'text'}
									icon={<ProfileOutlined />}
								>
									Výcviky
								</StyledButton>
							</Link>
							<Link to={ROUTES.APP.CALENDAR}>
								<StyledButton
									type={pathname.startsWith(ROUTES.APP.CALENDAR) ? 'link' : 'text'}
									icon={<CalendarOutlined />}
								>
									Kalendář
								</StyledButton>
							</Link>
							<Link to={ROUTES.APP.REPORTING}>
								<StyledButton
									type={pathname.startsWith(ROUTES.APP.REPORTING) ? 'link' : 'text'}
									icon={<IdcardOutlined />}
								>
									Hlášenky
								</StyledButton>
							</Link>
							<Link to={ROUTES.APP.TEACHER_STATISTICS}>
								<StyledButton
									type={pathname.startsWith(ROUTES.APP.TEACHER_STATISTICS) ? 'link' : 'text'}
									icon={<BarChartOutlined />}
								>
									Statistika jízd
								</StyledButton>
							</Link>
							<Link to={ROUTES.APP.EXAMS}>
								<StyledButton
									type={pathname.startsWith(ROUTES.APP.EXAMS) ? 'link' : 'text'}
									icon={<FileTextOutlined />}
								>
									Zkoušky
								</StyledButton>
							</Link>
							{featureFlags.DEBUG && (
								<Link to={ROUTES.APP.DEBUG}>
									<StyledButton
										type={pathname.startsWith(ROUTES.APP.DEBUG) ? 'link' : 'text'}
										icon={<BugOutlined />}
									>
										Debug
									</StyledButton>
								</Link>
							)}
							{featureFlags.EVIDIA_VERSION_2_1 && (
								<Link to={ROUTES.APP.USER_SETTINGS}>
									<StyledButton
										type={pathname.startsWith(ROUTES.APP.SETTINGS) ? 'link' : 'text'}
										icon={<SettingOutlined />}
									>
										Uživatelské Nastavení
									</StyledButton>
								</Link>
							)}
							{featureFlags.EVIDIA_VERSION_2_1 && (
								<Link to={ROUTES.APP.APPLICATION_FORM}>
									<StyledButton
										type={pathname.startsWith(ROUTES.APP.APPLICATION_FORM) ? 'link' : 'text'}
										icon={<SettingOutlined />}
									>
										Přihláškový formulář
									</StyledButton>
								</Link>
							)}
						</AsideSection>
						<AsideSection>
							{toggled && <UpgradeAccountWidget />}
							<Link to={ROUTES.APP.SUPPORT}>
								<StyledButton
									type={pathname.startsWith(ROUTES.APP.SUPPORT) ? 'link' : 'text'}
									icon={<QuestionCircleOutlined />}
								>
									Podpora
								</StyledButton>
							</Link>

							<StyledSettingsButton
								data-testid="menu-settings"
								type="text"
								icon={<SettingOutlined />}
								{...(getToggleProps() as any)}
							>
								Nastavení
								{!isExpanded ? <DownOutlined /> : <UpOutlined />}
							</StyledSettingsButton>
							<StyledSettingsSection {...getCollapseProps()}>
								<Link to={`${ROUTES.APP.SETTINGS}/info`} data-testid="menu-info">
									<StyledButton
										type={pathname.startsWith(`${ROUTES.APP.SETTINGS}/info`) ? 'link' : 'text'}
										size="small"
									>
										Informace
									</StyledButton>
								</Link>
								<Link to={`${ROUTES.APP.SETTINGS}/operator`} data-testid="menu-operator">
									<StyledButton
										type={pathname.startsWith(`${ROUTES.APP.SETTINGS}/operator`) ? 'link' : 'text'}
										size="small"
									>
										Provozovatel
									</StyledButton>
								</Link>

								<Link to={`${ROUTES.APP.SETTINGS}/address`} data-testid="menu-address">
									<StyledButton
										type={pathname.startsWith(`${ROUTES.APP.SETTINGS}/address`) ? 'link' : 'text'}
										size="small"
									>
										Adresa učebny
									</StyledButton>
								</Link>
								<Link to={`${ROUTES.APP.SETTINGS}/cars`} data-testid="menu-cars">
									<StyledButton
										type={pathname.startsWith(`${ROUTES.APP.SETTINGS}/cars`) ? 'link' : 'text'}
										size="small"
									>
										Vozidla
									</StyledButton>
								</Link>
								<Link to={ROUTES.APP.TEACHERS} data-testid="menu-teachers">
									<StyledButton type={pathname.startsWith(ROUTES.APP.TEACHERS) ? 'link' : 'text'}>
										Učitelé
									</StyledButton>
								</Link>
								<Link to={ROUTES.APP.INTERPRETER} data-testid="menu-interpreters">
									<StyledButton type={pathname.startsWith(ROUTES.APP.INTERPRETER) ? 'link' : 'text'}>
										Tlumočníci
									</StyledButton>
								</Link>
								<Link to={`${ROUTES.APP.SETTINGS}/tags`} data-testid="menu-tags">
									<StyledButton
										type={pathname.startsWith(`${ROUTES.APP.SETTINGS}/tags`) ? 'link' : 'text'}
										size="small"
									>
										Štítky
									</StyledButton>
								</Link>
								<Link to={`${ROUTES.APP.SETTINGS}/contact`} data-testid="menu-contact">
									<StyledButton
										type={pathname.startsWith(`${ROUTES.APP.SETTINGS}/contact`) ? 'link' : 'text'}
										size="small"
									>
										Kontakt
									</StyledButton>
								</Link>
								<Link to={`${ROUTES.APP.SETTINGS}/account`} data-testid="menu-account">
									<StyledButton
										type={pathname.startsWith(`${ROUTES.APP.SETTINGS}/account`) ? 'link' : 'text'}
										size="small"
									>
										Účet
									</StyledButton>
								</Link>
								<Link to={`${ROUTES.APP.TIER}`} data-testid="menu-tier">
									<StyledButton
										type={pathname.startsWith(`${ROUTES.APP.TIER}`) ? 'link' : 'text'}
										size="small"
									>
										Tarif
									</StyledButton>
								</Link>
								<Link to={`${ROUTES.APP.SETTINGS}/pdf-history`} data-testid="menu-pdf history">
									<StyledButton
										type={
											pathname.startsWith(`${ROUTES.APP.SETTINGS}/pdf-history`) ? 'link' : 'text'
										}
										size="small"
									>
										Historie PDF
									</StyledButton>
								</Link>

								<Link to={`${ROUTES.APP.SETTINGS}/student-pass`} data-testid="menu-pdf history">
									<StyledButton
										type={
											pathname.startsWith(`${ROUTES.APP.SETTINGS}/student-pass`) ? 'link' : 'text'
										}
										size="small"
									>
										Průkazky
									</StyledButton>
								</Link>
							</StyledSettingsSection>
						</AsideSection>
					</ScrollableContainer>
				</StyledAntdSidebar>
			)}
		</>
	);
};

export default SideBar;
